import type { PageSeo } from './types'

import { AssetPickerField } from './AssetPicker'
import { OptionSelect, numOrUndef } from './settingFields'

// Every field below is emitted by the static-site renderer
// (frontend/site-ssr/renderSite.tsx: head meta + sitemap.xml). Blank = the
// renderer's own default, which each placeholder names.
const OG_TYPES = ['website', 'article', 'product', 'profile', 'book', 'video.other', 'music.song']
  .map(v => ({ value: v, label: v }))
const CHANGEFREQS = ['always', 'hourly', 'daily', 'weekly', 'monthly', 'yearly', 'never']
  .map(v => ({ value: v, label: v }))
const ROBOTS = [
  { value: 'index,follow', label: 'index, follow' },
  { value: 'noindex,follow', label: 'noindex, follow' },
  { value: 'index,nofollow', label: 'index, nofollow' },
  { value: 'noindex,nofollow', label: 'noindex, nofollow' },
]

interface Props {
  project: string
  value?: PageSeo
  onChange: (v: PageSeo) => void
}

export function SeoPanel({ project, value, onChange }: Props) {
  const v = value ?? {}
  const set = (patch: Partial<PageSeo>) => onChange({ ...v, ...patch })
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Field label="Page title">
        <input type="text" value={v.title ?? ''} onChange={e => set({ title: e.target.value })} placeholder="Title shown in browser tab" style={inputStyle} />
      </Field>
      <Field label="Meta description">
        <textarea
          value={v.description ?? ''}
          onChange={e => set({ description: e.target.value })}
          placeholder="One- or two-sentence summary for search engines (≤160 chars)"
          rows={3}
          style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
        />
      </Field>

      <h4 style={{ fontSize: 13, fontWeight: 600, margin: '6px 0', color: 'var(--color-text-secondary)' }}>Open Graph (social cards)</h4>
      <Field label="OG title">
        <input type="text" value={v.ogTitle ?? ''} onChange={e => set({ ogTitle: e.target.value })} placeholder="Falls back to page title" style={inputStyle} />
      </Field>
      <Field label="OG description">
        <input type="text" value={v.ogDescription ?? ''} onChange={e => set({ ogDescription: e.target.value })} placeholder="Falls back to meta description" style={inputStyle} />
      </Field>
      <AssetPickerField project={project} value={v.ogImage} onChange={url => set({ ogImage: url })} label="OG image (1200×630 recommended)" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label="OG type">
          <OptionSelect
            value={v.ogType}
            options={OG_TYPES}
            unsetLabel="website (default)"
            onChange={ogType => set({ ogType })}
            style={inputStyle}
          />
        </Field>
        <Field label="OG URL">
          <input type="text" value={v.ogUrl ?? ''} onChange={e => set({ ogUrl: e.target.value.trim() || undefined })} placeholder="Falls back to canonical URL" style={inputStyle} />
        </Field>
      </div>

      <Field label="Twitter card">
        {/* Unset is NOT summary_large_image: the renderer picks a large card
            only when the page has an image (a large card without one renders
            badly), so "auto" is its own choice. */}
        <OptionSelect
          value={v.twitterCard}
          options={[
            { value: 'summary', label: 'summary' },
            { value: 'summary_large_image', label: 'summary_large_image' },
          ]}
          unsetLabel="Auto (large when an image is set)"
          onChange={tc => set({ twitterCard: tc as PageSeo['twitterCard'] })}
          style={inputStyle}
        />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label="Twitter site">
          <input type="text" value={v.twitterSite ?? ''} onChange={e => set({ twitterSite: e.target.value.trim() || undefined })} placeholder="@yourbrand" style={inputStyle} />
        </Field>
        <Field label="Twitter creator">
          <input type="text" value={v.twitterCreator ?? ''} onChange={e => set({ twitterCreator: e.target.value.trim() || undefined })} placeholder="@author" style={inputStyle} />
        </Field>
      </div>

      <h4 style={{ fontSize: 13, fontWeight: 600, margin: '6px 0', color: 'var(--color-text-secondary)' }}>Indexing</h4>
      <Field label="Canonical URL">
        <input type="text" value={v.canonical ?? ''} onChange={e => set({ canonical: e.target.value })} placeholder="https://example.com/this-page" style={inputStyle} />
      </Field>
      <Field label="Robots">
        {/* A hand-written value (e.g. 'noindex') is shown as unrecognised
            rather than misreported as 'index, follow'. */}
        <OptionSelect
          value={v.robots}
          options={ROBOTS}
          unsetLabel="Not set (no robots meta — crawlers index & follow)"
          onChange={robots => set({ robots })}
          style={inputStyle}
        />
      </Field>

      <h4 style={{ fontSize: 13, fontWeight: 600, margin: '6px 0', color: 'var(--color-text-secondary)' }}>Sitemap</h4>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
        <input type="checkbox" checked={!!v.noSitemap} onChange={e => set({ noSitemap: e.target.checked || undefined })} />
        Exclude this page from sitemap.xml
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, opacity: v.noSitemap ? 0.5 : 1 }}>
        <Field label="Change frequency">
          <OptionSelect
            value={v.changefreq}
            options={CHANGEFREQS}
            unsetLabel="Not set"
            disabled={!!v.noSitemap}
            onChange={cf => set({ changefreq: cf as PageSeo['changefreq'] })}
            style={inputStyle}
          />
        </Field>
        <Field label="Priority (0.0–1.0)">
          <input
            type="number" min={0} max={1} step={0.1}
            value={v.priority ?? ''}
            disabled={!!v.noSitemap}
            onChange={e => {
              const n = numOrUndef(e.target.value)
              set({ priority: n === undefined ? undefined : Math.max(0, Math.min(1, n)) })
            }}
            placeholder="Not set"
            style={inputStyle}
          />
        </Field>
      </div>

      <Field label="JSON-LD (structured data)">
        <textarea
          value={v.jsonLd ? JSON.stringify(v.jsonLd, null, 2) : ''}
          onChange={e => {
            try { set({ jsonLd: e.target.value ? JSON.parse(e.target.value) : undefined }) }
            catch { /* keep raw text in DOM, ignore until valid */ }
          }}
          placeholder={'{ "@context": "https://schema.org", "@type": "WebPage" }'}
          rows={6}
          style={{ ...inputStyle, fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 13 }}
        />
      </Field>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </div>
  )
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 14,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
